// Choosing a converted skeleton's states that are skins: rive-spine's companion runtime (`runtime/riveSpineSkins.ts`,
// copied 2026-10-11, unchanged but for this header; the engine's copy since decision 72).
//
// A Rive state machine layer whose states only hold opacities (a colour switch, a pot's level) becomes skins: each
// state's skin carries its images, their held opacity in the attachment colour, and the bones they need (skipped while
// the skin is off). Several such layers (one per pot) are shown together by combining their skins.
//
// Each slot then shows its setup attachment as the new skins have it, or nothing; slot colours (what animations set)
// are left alone, so a switch mid-animation changes nothing else — as in Rive, where the switch is instant.
import { Skin, type Skeleton } from "@esotericsoftware/spine-pixi-v8";

const combined = new WeakMap<Skeleton["data"], Map<string, Skin>>();

/** Shows `names`' skins together (one name: that skin), re-attaching every slot from them. */
export function setSkins(skeleton: Skeleton, names: readonly string[]): void {
  const data = skeleton.data;
  let skin: Skin;
  if (names.length === 1) {
    const found = data.findSkin(names[0]!);
    if (!found) throw new Error(`no skin "${names[0]}"`);
    skin = found;
  } else {
    const key = names.join("+");
    let cache = combined.get(data);
    if (!cache) combined.set(data, (cache = new Map()));
    let found = cache.get(key);
    if (!found) {
      found = new Skin(key);
      for (const name of names) {
        const part = data.findSkin(name);
        if (!part) throw new Error(`no skin "${name}"`);
        found.addSkin(part);
      }
      cache.set(key, found);
    }
    skin = found;
  }
  skeleton.setSkin(skin);
  for (const slot of skeleton.slots) {
    const name = slot.data.attachmentName;
    slot.setAttachment(name ? skeleton.getAttachment(slot.data.index, name) : null);
  }
}
